import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ConsumerUnitContext = createContext(null);

const LS_KEY = 'nn-selected-unit-id';

// Inline unit list — will be replaced with API fetch when org-hierarchy endpoint is live
const CONSUMER_UNITS = [
  { id: 'all', name: 'All Units', mobName: null },
  { id: 'a0000000-0000-4000-8000-000000000101', name: 'Main St Clinic', mobName: 'Eastside MOB' },
  { id: 'a0000000-0000-4000-8000-000000000102', name: 'Oak Park Family', mobName: 'Eastside MOB' },
  { id: 'a0000000-0000-4000-8000-000000000103', name: 'Riverside Urgent', mobName: 'Westside MOB' },
];

/**
 * Global unit selection context for the Manager role.
 * Wraps AppShell so the Header and all manager pages share the same unit scope.
 *
 * - fixedUnitId: locks to a single unit (Director drill-down)
 * - If only 1 real unit exists, defaults to that unit (no "All Units" option)
 */
function ConsumerUnitProvider({ children, fixedUnitId }) {
  const allUnits = CONSUMER_UNITS;
  const realUnits = allUnits.filter((u) => u.id !== 'all');
  const singleUnit = realUnits.length === 1;
  const defaultId = fixedUnitId || (singleUnit ? realUnits[0].id : 'all');

  const [selectedUnitId, setSelectedUnitId] = useState(() => {
    if (fixedUnitId) return defaultId;
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored && allUnits.some((u) => u.id === stored)) return stored;
    } catch { /* SSR / private browsing */ }
    return defaultId;
  });
  const activeUnitId = fixedUnitId || selectedUnitId;

  // Persist selection (skip for Director drill-down)
  useEffect(() => {
    if (fixedUnitId) return;
    try { localStorage.setItem(LS_KEY, activeUnitId); } catch { /* noop */ }
  }, [activeUnitId, fixedUnitId]);

  const selectedUnit = useMemo(
    () => allUnits.find((u) => u.id === activeUnitId) || allUnits[0],
    [allUnits, activeUnitId],
  );

  const value = useMemo(
    () => ({
      units: singleUnit ? realUnits : allUnits,
      selectedUnitId: activeUnitId,
      setSelectedUnitId: fixedUnitId ? () => {} : setSelectedUnitId,
      selectedUnit,
      isFixed: !!fixedUnitId,
      singleUnit,
    }),
    [allUnits, realUnits, activeUnitId, fixedUnitId, selectedUnit, singleUnit, setSelectedUnitId],
  );

  return (
    <ConsumerUnitContext.Provider value={value}>
      {children}
    </ConsumerUnitContext.Provider>
  );
}

function useConsumerUnit() {
  const ctx = useContext(ConsumerUnitContext);
  if (!ctx) throw new Error('useConsumerUnit must be used within ConsumerUnitProvider');
  return ctx;
}

export { ConsumerUnitContext, ConsumerUnitProvider, useConsumerUnit };
